import React, { createContext, useEffect, useMemo, useReducer, ReactNode } from 'react';

import { ISendData } from '../api/modules/session';
import { fetchAuthToken, setAuthToken } from '../api/tokens';
import api from '../api';

export const exchangeTokenSearchParamName = 'exchangeToken';

export type AuthData = {
  token: null | string;
  isLoading: boolean;
};

export type AuthContextData = {
  state: AuthData;
  exchangeToken(token: string): Promise<void>;
};

export const AuthContext = createContext<AuthContextData>({} as AuthContextData);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [state, dispatch] = useReducer(
    (prevState: any, action: any) => {
      switch (action.type) {
        case 'SET_TOKEN':
          return {
            ...prevState,
            token: action.token,
            isLoading: false,
          };
        case 'IS_LOADING':
          return {
            ...prevState,
            isLoading: action.isLoading,
          };
      }
    },
    {
      token: null,
      isLoading: true,
    },
  );

  const authContext = useMemo(
    () => ({
      state,
      exchangeToken: async (token: string) => {
        const data = { exchangeToken: token } as ISendData;
        const { bearerToken } = await api.session.exchangeToken(data);

        setAuthToken(bearerToken);
        dispatch({ type: 'SET_TOKEN', token: bearerToken });
      },
    }),
    [state],
  );

  useEffect(() => {
    const bootstrapAsync = async () => {
      dispatch({ type: 'IS_LOADING', isLoading: true });
      let token = fetchAuthToken();

      try {
        const url = new URL(window.location.href);
        const query = url.searchParams;
        const exchangeToken = query.get(exchangeTokenSearchParamName);

        if (exchangeToken) {
          const { bearerToken } = await api.session.exchangeToken({ exchangeToken });
          token = bearerToken;
          setAuthToken(bearerToken);
        }
      } catch (e) {
        token = null;
      } finally {
        dispatch({ type: 'SET_TOKEN', isLoading: false, token });
      }
    };

    bootstrapAsync();
  }, []);

  return <AuthContext.Provider value={authContext}>{children}</AuthContext.Provider>;
};

export default {
  AuthContext,
  AuthProvider,
};
